import { addDoc, collection, doc, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import { loadRecordsByEquality } from './firestoreQueryHelpers';
import { validateMemberAuthLink } from './memberAuthLink';
import { identityEmailsMatch } from './identityEmail';
import {
  onOrganizationInviteAccepted,
  onOrganizationInviteCreated,
  onOrganizationInviteDeclined,
  onOrganizationMembershipActivated,
} from './organizationInviteHooks';

function randomTokenPart() {
  return Math.random().toString(36).slice(2, 10);
}

export function normalizeInviteEmail(email = '') {
  // Pass 86C: trim + lowercase, and strip any stray TRAILING dot(s). A trailing dot (e.g.
  // "name@example.com." from a data-entry slip) is not a valid `to` address and is rejected by the email
  // provider (Resend HTTP 422). Stripping it preserves the SAME mailbox — it is format hygiene, not redirection.
  return String(email || '').trim().toLowerCase().replace(/\.+$/, '');
}

export function buildOrganizationInviteCode() {
  return `orginv_${Date.now().toString(36)}_${randomTokenPart()}`;
}

export function normalizeOrganizationInvite(record = {}, fallbackId = '') {
  const id = record.id || record.inviteId || fallbackId || '';
  const email = record.email || '';
  const emailNormalized = normalizeInviteEmail(record.emailNormalized || email);
  const inviteCode = record.inviteCode || record.token || '';

  return {
    id,
    inviteId: id,
    organizationId: record.organizationId || '',
    organizationName: record.organizationName || '',
    email,
    emailNormalized,
    role: record.role || 'instructor',
    status: record.status || 'pending',
    invitedByUserId: record.invitedByUserId || '',
    invitedByName: record.invitedByName || '',
    inviteCode,
    createdAt: record.createdAt || null,
    updatedAt: record.updatedAt || null,
    acceptedAt: record.acceptedAt || null,
    declinedAt: record.declinedAt || null,
    expiresAt: record.expiresAt || null,
    linkedUserId: record.linkedUserId || '',
    note: record.note || '',
    // Pass 55 Stage D — an invite may explicitly target an existing canonical member (e.g. a seeded
    // instructor). Acceptance then LINKS that member (sets authUserId, preserves userId).
    targetMemberId: record.targetMemberId || '',
    targetMemberUserId: record.targetMemberUserId || '',
  };
}

export function isOrganizationInviteExpired(invite = {}) {
  const expiresAt = invite?.expiresAt;
  if (!expiresAt) return false;

  const expiryTime = expiresAt?.seconds
    ? expiresAt.seconds * 1000
    : new Date(expiresAt).getTime();

  return Number.isFinite(expiryTime) && expiryTime < Date.now();
}

export async function upsertOrganizationMembershipFromInvite(
  db,
  invite,
  { currentUser = null, userData = {} } = {}
) {
  const membershipRecords = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.ORGANIZATION_MEMBERS,
    field: 'organizationId',
    value: invite.organizationId,
  });

  const normalizedEmail = normalizeInviteEmail(currentUser?.email || userData?.email || invite.email);

  // Pass 55 Stage E — an invite MAY target an existing canonical (e.g. seeded) member EXPLICITLY via
  // targetMemberUserId and/or targetMemberId. When an explicit target is present it is authoritative:
  // it must resolve to exactly one member and MUST FAIL CLOSED if the target is missing/ambiguous —
  // it may NOT silently fall through to uid/email matching or new-member creation. Only when there is
  // NO explicit target does the legacy uid/email lookup (and new-member creation) apply.
  const hasExplicitTarget = !!(invite.targetMemberUserId || invite.targetMemberId);
  let existingMembership;
  if (hasExplicitTarget) {
    const byUserId = invite.targetMemberUserId
      ? membershipRecords.find((m) => m.userId === invite.targetMemberUserId) : null;
    const byDocId = invite.targetMemberId
      ? membershipRecords.find((m) => m.id === invite.targetMemberId) : null;
    if (invite.targetMemberUserId && invite.targetMemberId) {
      // both supplied → they MUST resolve to the SAME canonical member
      if (!byUserId || !byDocId || byUserId.id !== byDocId.id) {
        const err = new Error('Cannot link member: target_member_conflict');
        err.code = 'MEMBER_LINK_BLOCKED'; err.blockers = ['target_member_conflict']; throw err;
      }
      existingMembership = byUserId;
    } else {
      existingMembership = byUserId || byDocId;
    }
    if (!existingMembership?.id) {
      const err = new Error('Cannot link member: target_member_missing');
      err.code = 'MEMBER_LINK_BLOCKED'; err.blockers = ['target_member_missing']; throw err;
    }
  } else {
    // Pass 88.4 — match an existing member by uid, or by the SAME login mailbox under the narrow identity
    // canonicalization (gmail.com ≡ googlemail.com). Still exact otherwise; no fuzzy matching.
    existingMembership = membershipRecords.find((membership) => membership.userId === currentUser?.uid
      || identityEmailsMatch(membership.email, normalizedEmail));
  }

  if (existingMembership?.id) {
    // LINK an EXISTING member: preserve its permanent userId + business fields; set authUserId ONLY.
    // Fail-closed — never overwrite userId, never cross-link Auth UIDs. (memberAuthLink is the guard.)
    const authUserId = currentUser?.uid || existingMembership.authUserId || '';
    const link = validateMemberAuthLink({
      member: existingMembership, authUserId, actorEmail: currentUser?.email || userData?.email || '',
      organizationId: invite.organizationId, existingMembers: membershipRecords,
    });
    if (!link.ok) { const err = new Error(`Cannot link member: ${link.blockers.join(', ')}`); err.code = 'MEMBER_LINK_BLOCKED'; err.blockers = link.blockers; throw err; }
    const linkPayload = { status: 'active', authUserId, activationInviteCode: invite.inviteCode || '', updatedAt: serverTimestamp() };
    await updateDoc(doc(db, PLATFORM_COLLECTIONS.ORGANIZATION_MEMBERS, existingMembership.id), linkPayload);
    // userId is deliberately NOT in linkPayload → preserved.
    const normalizedMembership = { ...existingMembership, ...linkPayload };
    await onOrganizationMembershipActivated(db, normalizedMembership, invite);
    return normalizedMembership;
  }

  // NEW membership (no existing canonical member): a brand-new login-owned member uses uid as userId.
  const payload = {
    organizationId: invite.organizationId,
    organizationName: invite.organizationName,
    userId: currentUser?.uid || '',
    authUserId: currentUser?.uid || '',
    email: currentUser?.email || userData?.email || invite.email,
    name: userData?.name || currentUser?.displayName || '',
    role: invite.role,
    status: 'active',
    activationInviteCode: invite.inviteCode || '',
    updatedAt: serverTimestamp(),
  };

  const membershipRef = await addDoc(collection(db, PLATFORM_COLLECTIONS.ORGANIZATION_MEMBERS), {
    ...payload,
    createdAt: serverTimestamp(),
  });
  const normalizedMembership = { id: membershipRef.id, ...payload };
  await onOrganizationMembershipActivated(db, normalizedMembership, invite);
  return normalizedMembership;
}

export async function getOrganizationInvites(db, organizationId = '') {
  const records = organizationId
    ? await loadRecordsByEquality(db, {
        collectionName: PLATFORM_COLLECTIONS.ORGANIZATION_INVITES,
        field: 'organizationId',
        value: organizationId,
      })
    : [];

  return records.map((record) => normalizeOrganizationInvite(record));
}

export async function getPendingOrganizationInvitesForOrganization(db, organizationId = '') {
  const invites = await getOrganizationInvites(db, organizationId);
  return invites.filter((invite) => invite.status === 'pending');
}

export async function getPendingOrganizationInvitesForEmail(db, email = '') {
  const emailNormalized = normalizeInviteEmail(email);
  if (!emailNormalized) return [];

  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.ORGANIZATION_INVITES,
    field: 'emailNormalized',
    value: emailNormalized,
  });

  return records
    .map((record) => normalizeOrganizationInvite(record))
    .filter((invite) => invite.status === 'pending');
}

export async function getOrganizationInviteByCode(db, inviteCode = '') {
  if (!inviteCode) return null;

  try {
    const inviteSnapshot = await getDoc(doc(db, PLATFORM_COLLECTIONS.ORGANIZATION_INVITES, inviteCode));
    if (inviteSnapshot.exists()) {
      return normalizeOrganizationInvite(
        {
          id: inviteSnapshot.id,
          ...inviteSnapshot.data(),
        },
        inviteSnapshot.id
      );
    }
  } catch (error) {
    console.warn('Falling back from direct invite lookup:', error);
  }

  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.ORGANIZATION_INVITES,
    field: 'inviteCode',
    value: inviteCode,
  });

  return normalizeOrganizationInvite(records[0] || {});
}

export async function createOrganizationInvite(
  db,
  {
    organizationId = '',
    organizationName = '',
    email = '',
    role = 'instructor',
    invitedByUserId = '',
    invitedByName = '',
    note = '',
    expiresAt = null,
    // Pass 55 Stage D — optionally target an EXISTING canonical member (seeded instructor). On accept
    // that member is LINKED (authUserId set, userId preserved), never duplicated.
    targetMemberId = '',
    targetMemberUserId = '',
  } = {}
) {
  const emailNormalized = normalizeInviteEmail(email);
  if (!organizationId || !emailNormalized) {
    throw new Error('organizationId and email are required');
  }

  const existingInvites = await getPendingOrganizationInvitesForOrganization(db, organizationId);
  const duplicateInvite = existingInvites.find(
    (invite) => invite.emailNormalized === emailNormalized && invite.role === role
  );

  if (duplicateInvite) {
    return { ...duplicateInvite, duplicate: true };
  }

  // OPTIONAL existing-user enrichment: if an Auth user already has this email we record `linkedUserId` so acceptance can
  // pre-associate it. This reads the `users` collection, which — by design — a non-platform-admin organization admin CANNOT
  // query under the security rules (users are readable only by self / same-org admins per-doc / platform admins). A
  // permission-denied here must NOT block a legitimate invite create (the invite authority is owner/admin of the org, which
  // the create rule independently enforces). So this lookup is best-effort: on ANY failure we degrade to "no linked user"
  // (linkedUserId '') — identical to the common case of inviting a brand-new email that has no Auth user yet. The canonical
  // accept path links by the accepting user's authenticated uid regardless, so `linkedUserId` is only a convenience hint.
  let matchingUsers = [];
  let normalizedMatchingUsers = [];
  try {
    [matchingUsers, normalizedMatchingUsers] = await Promise.all([
      loadRecordsByEquality(db, {
        collectionName: 'users',
        field: 'email',
        value: email,
      }),
      email !== emailNormalized
        ? loadRecordsByEquality(db, {
            collectionName: 'users',
            field: 'email',
            value: emailNormalized,
          })
        : Promise.resolve([]),
    ]);
  } catch (enrichmentError) {
    console.warn('createOrganizationInvite: skipping optional existing-user enrichment (users not readable):', enrichmentError?.message || enrichmentError);
    matchingUsers = [];
    normalizedMatchingUsers = [];
  }
  const linkedUser = [...matchingUsers, ...normalizedMatchingUsers].find((user) => user?.id);

  const inviteCode = buildOrganizationInviteCode();
  const payload = {
    organizationId,
    organizationName,
    email,
    emailNormalized,
    role,
    status: 'pending',
    invitedByUserId,
    invitedByName,
    inviteCode,
    linkedUserId: linkedUser?.id || '',
    note,
    targetMemberId,
    targetMemberUserId,
    expiresAt: expiresAt || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const inviteRef = doc(collection(db, PLATFORM_COLLECTIONS.ORGANIZATION_INVITES), inviteCode);
  await setDoc(inviteRef, payload);
  const normalizedInvite = normalizeOrganizationInvite({
    id: inviteCode,
    ...payload,
  });
  await onOrganizationInviteCreated(db, normalizedInvite);
  return normalizedInvite;
}

// Pass 58B — a REAL canonical resend. Reuses the EXISTING pending canonical invite (same inviteCode,
// same targetMember + approved email/role), stamps resentAt + actor, and re-fires the delivery hook.
// If no canonical invite exists yet it can be (re)created by the caller via createOrganizationInvite.
// HONEST LIMITATION: outbound EMAIL delivery is NOT implemented (onOrganizationInviteCreated only writes
// an in-app notification, and only when the invitee already has a users account). "Resend" therefore
// re-issues the in-app notification / shareable /invite/<code> link, not an email.
export async function resendOrganizationInvite(db, { inviteCode = '', organizationId = '', email = '', actorUserId = '' } = {}) {
  let invite = inviteCode ? await getOrganizationInviteByCode(db, inviteCode) : null;
  if (!invite && organizationId && email) {
    const pending = await getPendingOrganizationInvitesForOrganization(db, organizationId);
    const normEmail = normalizeInviteEmail(email);
    invite = pending.find((i) => i.emailNormalized === normEmail) || null;
  }
  if (!invite) {
    const e = new Error('No pending canonical invite to resend.');
    e.code = 'NO_PENDING_INVITE';
    throw e;
  }
  const code = invite.inviteCode || invite.id;
  await updateDoc(doc(db, PLATFORM_COLLECTIONS.ORGANIZATION_INVITES, code), {
    resentAt: serverTimestamp(), resentByUserId: actorUserId, updatedAt: serverTimestamp(),
  });
  await onOrganizationInviteCreated(db, normalizeOrganizationInvite({ ...invite, id: code }));
  return { inviteCode: code, resent: true, emailDeliveryImplemented: false };
}

export async function acceptOrganizationInvite(
  db,
  {
    inviteCode = '',
    currentUser = null,
    userData = {},
  } = {}
) {
    const invite = await getOrganizationInviteByCode(db, inviteCode);
    if (!invite?.inviteId) {
      throw new Error('Invite not found');
    }

    if (invite.status !== 'pending') {
      return invite;
    }

    if (isOrganizationInviteExpired(invite)) {
      await expireOrganizationInvite(db, { inviteId: invite.inviteId });
      return { ...invite, status: 'expired' };
    }

    // Pass 88.4 — equivalence under the narrow identity canonicalization (gmail.com ≡ googlemail.com).
    // The stored emails are NOT rewritten; this is the acceptance-gate comparison only.
    const currentEmail = currentUser?.email || userData?.email || '';
    if (!currentEmail || !identityEmailsMatch(currentEmail, invite.emailNormalized || invite.email)) {
      throw new Error('Invite email does not match the signed-in user');
    }

    const membership = await upsertOrganizationMembershipFromInvite(db, invite, {
      currentUser,
      userData,
    });

    await updateDoc(doc(db, PLATFORM_COLLECTIONS.ORGANIZATION_INVITES, invite.inviteId), {
      status: 'accepted',
      linkedUserId: currentUser?.uid || '',
      acceptedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const acceptedInvite = {
      ...invite,
      status: 'accepted',
      linkedUserId: currentUser?.uid || '',
    };
  await onOrganizationInviteAccepted(db, acceptedInvite, membership);
  return acceptedInvite;
}

export async function declineOrganizationInvite(
  db,
  {
    inviteCode = '',
    currentUser = null,
    userData = {},
  } = {}
) {
  const invite = await getOrganizationInviteByCode(db, inviteCode);
  if (!invite?.inviteId) {
    throw new Error('Invite not found');
  }

  if (invite.status !== 'pending') {
    return invite;
  }

  const currentEmail = currentUser?.email || userData?.email || '';
  if (currentEmail && !identityEmailsMatch(currentEmail, invite.emailNormalized || invite.email)) {
    throw new Error('Invite email does not match the signed-in user');
  }

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.ORGANIZATION_INVITES, invite.inviteId), {
    status: 'declined',
    linkedUserId: currentUser?.uid || invite.linkedUserId || '',
    declinedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  const declinedInvite = {
    ...invite,
    status: 'declined',
    linkedUserId: currentUser?.uid || invite.linkedUserId || '',
  };
  await onOrganizationInviteDeclined(db, declinedInvite);
  return declinedInvite;
}

export async function cancelOrganizationInvite(db, { inviteId = '' } = {}) {
  if (!inviteId) throw new Error('inviteId is required');

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.ORGANIZATION_INVITES, inviteId), {
    status: 'canceled',
    updatedAt: serverTimestamp(),
  });
}

export async function expireOrganizationInvite(db, { inviteId = '' } = {}) {
  if (!inviteId) throw new Error('inviteId is required');

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.ORGANIZATION_INVITES, inviteId), {
    status: 'expired',
    updatedAt: serverTimestamp(),
  });
}
